import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Paperclip } from "lucide-react";

import { PageHeader } from "@/components/dashboard/page-header";
import { StatusPill } from "@/components/dashboard/status-pill";
import { PersonAvatar } from "@/components/employees/person-avatar";
import { DecisionPanel } from "@/components/leave/decision-panel";
import {
  leave as copy,
  leaveGroups,
  nav,
  status as statusCopy,
} from "@/content";
import { can, scopeToSupervisorId } from "@/constants/permissions";
import { getLeaveRequest, leaveBalance, type LeaveBalanceRow } from "@/api/leave";
import { cn } from "@/utils";
import { localizedName } from "@/utils/locale";
import { useLocale } from "@/context/locale-provider";
import { useQuery } from "@tanstack/react-query";
import { useFmt } from "@/hooks/shared/useFmt";
import { useSession } from "@/context/UserContext";
import { RecordNotFound } from "@/components/dashboard/record-not-found";
import { SkeletonPanel } from "@/components/dashboard/skeletons";
import { AccessDenied } from "@/components/dashboard/access-denied";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { apiUrl } from "@/config";

/**
 * One leave request, in full.
 *
 * The prototype had no detail view at all. Its API serialised `createdAt` and
 * `decidedAt` on every payload and rendered neither, so on the screen whose whole
 * purpose was deciding requests you could not see when one was filed, when it was
 * decided, by whom, or why it was refused.
 *
 * Everything that was on the wire and invisible is here: the timeline, the
 * decider, the rejection reason, the attachment, and the guard's approved leave
 * so far this year as context for the decision.
 */
export const Route = createFileRoute("/_main/dashboard/leave/$id")({
  component: () => (
    <RequirePermission action="leave.read">
      <LeaveDetailPage />
    </RequirePermission>
  ),
});

function LeaveDetailPage() {
  useDocumentTitle(copy.requestTitle);
  const { user } = useSession();
  const { locale } = useLocale();
  const { id } = Route.useParams();
  const requestId = Number(id);
  const validId = Number.isInteger(requestId) && requestId >= 1;

  const fmt = useFmt();

  const requestQuery = useQuery({
    queryKey: ["leave", "get", requestId],
    queryFn: () => getLeaveRequest(requestId),
    enabled: validId,
  });
  const request = requestQuery.data;

  /* Keyed on the guard rather than the request, because it is the guard's balance
     — two requests from the same person share the answer. */
  const balanceQuery = useQuery({
    queryKey: ["leave", "balance", request?.guardId],
    queryFn: () => leaveBalance(request!.guardId),
    enabled: Boolean(request),
  });

  if (!validId || (requestQuery.isFetched && !request)) return <RecordNotFound />;
  if (!request || !balanceQuery.data) {
    return (
      <div className="mx-auto w-full max-w-shell px-6 py-8">
        <SkeletonPanel height="24rem" />
      </div>
    );
  }

  /* A supervisor opens only their own queue. Checked here as well as in the list
     query, because a URL can be typed and filtering a row out of a list is not
     access control. The API refuses it too. */
  const scope = scopeToSupervisorId(user);
  if (scope !== null && request.supervisorId !== scope) {
    return <AccessDenied role={user.role} />;
  }

  const balance = balanceQuery.data;

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader
        eyebrow={nav.groups.operations}
        title={request.guardName}
        lead={`${localizedName(request.leaveType.nameAr, request.leaveType.nameEn, locale)} · ${leaveGroups[request.leaveType.group]}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/dashboard/leave"
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-n4 bg-n0 px-4 text-sm font-semibold text-n8 transition-colors duration-200 ease-brand hover:bg-n2"
            >
              {/* Points the way the reader travels: "back" in RTL is rightward. */}
              <ArrowRight aria-hidden className="size-4" strokeWidth={1.6} />
              {copy.backToQueue}
            </Link>
            <DecisionPanel
              id={request.id}
              guardId={request.guardId}
              guardName={request.guardName}
              status={request.status}
              requiresAttachment={request.leaveType.requiresAttachment}
              canDecide={can(user.role, "leave.decide")}
              canReopen={can(user.role, "leave.reopen")}
            />
          </div>
        }
      />

      <div className="mt-8 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]">
        <div className="min-w-0">
          <dl className="grid gap-px overflow-hidden rounded-lg bg-n4 sm:grid-cols-2">
            <Cell label={copy.columnStatus}>
              {request.status === "pending" ? (
                <StatusPill tone="attention">{statusCopy.pending}</StatusPill>
              ) : request.status === "approved" ? (
                <StatusPill tone="positive">{statusCopy.approved}</StatusPill>
              ) : (
                <StatusPill tone="negative">{statusCopy.rejected}</StatusPill>
              )}
            </Cell>

            <Cell label={copy.duration}>
              <span className="tabular">
                {fmt.count(request.daysToDeduct)} {copy.fileAvailableBalanceUnit}
              </span>
              {request.fromNow ? (
                <span className="ms-2 align-middle">
                  <StatusPill tone="attention">{copy.fromNow}</StatusPill>
                </span>
              ) : null}
            </Cell>

            <Cell label={copy.period} className="sm:col-span-2">
              <span className="tabular">{fmt.range(request.startAt, request.endAt)}</span>
            </Cell>

            <Cell label={copy.guard}>
              <span className="flex items-center gap-2.5">
                <PersonAvatar name={request.guardName} photoUrl={request.photoUrl} />
                <Link
                  to={`/dashboard/employees/${request.guardId}`}
                  className="font-semibold text-orange-700 transition-opacity duration-200 ease-brand hover:opacity-80"
                >
                  {request.guardName}
                </Link>
              </span>
              <span className="mt-0.5 block text-[11px] text-n6" dir="ltr">
                {request.guardEmail}
              </span>
            </Cell>

            <Cell label={copy.supervisor}>{request.supervisorName ?? "—"}</Cell>

            <Cell label={copy.notes} className="sm:col-span-2">
              {request.notes ? (
                <span className="leading-body">{request.notes}</span>
              ) : (
                <span className="text-n6">{copy.noNotes}</span>
              )}
            </Cell>

            {/* 404'd in the prototype: a bare `/uploads/...` path resolved against
                the frontend origin, and the file itself was served publicly and
                unauthenticated. It now goes through a route handler that
                authorises the reader and presigns a short-lived URL. */}
            <Cell label={copy.attachment} className="sm:col-span-2">
              {request.attachmentKey ? (
                <a
                  href={apiUrl(`/api/files/${encodeURIComponent(request.attachmentKey)}`)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 font-semibold text-orange-700 transition-opacity duration-200 ease-brand hover:opacity-80"
                >
                  <Paperclip aria-hidden className="size-4" strokeWidth={1.6} />
                  {copy.hasAttachment}
                </a>
              ) : (
                <span className="text-n6">{copy.noAttachment}</span>
              )}
            </Cell>

            {request.rejectReason ? (
              <Cell label={copy.rejectReasonLabel} className="sm:col-span-2">
                <span className="leading-body text-destructive">{request.rejectReason}</span>
              </Cell>
            ) : null}
          </dl>

          {/* The timeline that was on the wire and rendered nowhere. */}
          <section className="mt-8">
            <h2 className="text-sm font-semibold text-n8">{copy.timelineTitle}</h2>
            <ol className="mt-4 flex flex-col gap-4 border-s border-n3 ps-5">
              <TimelineItem
                label={copy.timelineFiled}
                when={fmt.dateTime(request.createdAt)}
                who={request.guardName}
              />
              {request.decidedAt ? (
                <TimelineItem
                  label={copy.timelineDecided}
                  when={fmt.dateTime(request.decidedAt)}
                  who={request.decidedByName}
                  detail={
                    request.status === "approved" ? statusCopy.approved : statusCopy.rejected
                  }
                />
              ) : null}
              {request.reopenedAt ? (
                <TimelineItem
                  label={copy.timelineReopened}
                  when={fmt.dateTime(request.reopenedAt)}
                  who={request.reopenedByName}
                />
              ) : null}
            </ol>
          </section>
        </div>

        {/* Context for the decision. A supervisor approving a fourth emergency
            leave in a month had no way to know it was the fourth. */}
        <aside className="rounded-lg border border-n3 bg-n0 p-5 shadow-1">
          <h2 className="text-sm font-semibold text-n8">{copy.balanceTitle}</h2>
          {balance.types.length === 0 ? (
            <p className="mt-4 text-xs text-n6">{copy.balanceEmpty}</p>
          ) : (
            <dl className="mt-4 flex flex-col gap-3">
              <div className="flex items-baseline justify-between gap-3 pb-1 text-[11px] font-semibold tracking-[0.04em] text-n6">
                <dt>{copy.balanceType}</dt>
                <dd>{copy.balanceRemaining}</dd>
              </div>
              {balance.types.map((row: LeaveBalanceRow) => (
                <Balance
                  key={row.leaveType.id}
                  label={localizedName(row.leaveType.nameAr, row.leaveType.nameEn, locale)}
                  value={balanceLine(row, fmt)}
                />
              ))}
              <Balance label={copy.balanceRequests} value={fmt.count(balance.requests)} />
            </dl>
          )}
          <p className="mt-4 text-[11px] leading-snug text-n6">{copy.balanceHint}</p>
        </aside>
      </div>
    </div>
  );
}

function Cell({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("bg-n0 p-5", className)}>
      <dt className="text-[11px] font-semibold tracking-[0.04em] text-n6">{label}</dt>
      <dd className="mt-2 text-sm text-n8">{children}</dd>
    </div>
  );
}

function balanceLine(row: LeaveBalanceRow, fmt: ReturnType<typeof useFmt>): string {
  if (row.employmentStartMissing) return copy.fileMissingEmploymentStart;
  if (row.remainingDays === null) return copy.balanceNoLimit;
  const available = `${fmt.count(row.remainingDays)} ${copy.fileAvailableBalanceUnit}`;
  if (row.annualEntitlementDays === null) return available;
  return `${available} · ${copy.fileAnnualEntitlement} ${fmt.count(row.annualEntitlementDays)} ${copy.fileAvailableBalanceUnit}`;
}

function Balance({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-n3 pb-2 last:border-0 last:pb-0">
      <dt className="text-xs text-n7">{label}</dt>
      <dd className="tabular text-sm font-bold text-n9">{value}</dd>
    </div>
  );
}

function TimelineItem({
  label,
  when,
  who,
  detail,
}: {
  label: string;
  when: string;
  who?: string | null;
  detail?: string;
}) {
  return (
    <li className="relative">
      {/* The 45° diamond is the design system's list marker, not a dot. */}
      <span aria-hidden className="absolute -start-[27px] top-1.5 size-1.5 rotate-45 bg-orange" />
      <p className="text-xs font-semibold text-n8">
        {label}
        {detail ? <span className="ms-2 font-normal text-n6">{detail}</span> : null}
      </p>
      <p className="tabular mt-0.5 text-[11px] text-n6">
        {when}
        {who ? ` · ${copy.timelineBy} ${who}` : ""}
      </p>
    </li>
  );
}
